import React from 'react';
import { useImpressionItem } from '@fiverr-private/impressionable';
import { Section, Stack } from '@fiverr-private/layout_components';
import { FiverrProLightLogo } from '@fiverr-private/visuals';
import { I18n, translate } from '@fiverr-private/i18n-react';
import { Text, Typography } from '@fiverr-private/typography';
import { Box } from '@fiverr-private/theme';
import { ListItem, UnorderedList } from '@fiverr-private/list';
import { Button } from '@fiverr-private/button';
import { Image } from '@fiverr-private/media';
import pathfinder from '@fiverr-private/pathfinder';
import { Domingo } from '@fiverr-private/domingo';
import { getContext } from '@fiverr-private/fiverr_context';
import { getImpressionEnrichment, sendBigQueryEvent, sendMixPanelEvent } from '../../utils/biEvents';
import { BQ_HOMEPAGE_CLICK, BQ_HOMEPAGE_IMP } from '../../utils/biEvents/events/bigQueryEvents';
import SafeComponent from '../../shared/SafeComponent';
import { MP_PRO_BANNER_CLICKED } from '../../utils/biEvents/events/mixPanelEvents';
import { FREELANCERS_BG, GRADIENT, MONEY_BACK_ICON, PRO_LINK_PATH, PRO_LINK_UTM_SOURCE } from './constants';
import styles from './styles.module.scss';

const getProLink = (): string => {
    const pathfinderUrl = pathfinder(
        'fiverr_pro_product_page',
        { slug: 'expert-sourcing-services' },
        { query: { utm_source: PRO_LINK_UTM_SOURCE } }
    );

    if (pathfinderUrl) {
        return pathfinderUrl;
    }
    const { url } = getContext();

    return `${Domingo(url).removePath().pro().url}${PRO_LINK_PATH}?utm_source=${PRO_LINK_UTM_SOURCE}`;
};

const ProBannerSection = () => {
    const [ref] = useImpressionItem(getImpressionEnrichment({ eventName: BQ_HOMEPAGE_IMP }));
    const proLink = getProLink();

    const onButtonClick = () => {
        sendBigQueryEvent({
            eventName: BQ_HOMEPAGE_CLICK,
            params: { elementName: 'pro_banner', elementType: 'button' },
        });

        sendMixPanelEvent({
            eventName: MP_PRO_BANNER_CLICKED,
            params: {},
        });
    };

    return (
        <Section paddingTop="0" paddingBottom={{ default: '10', sm: '12', md: '12', lg: '10' }}>
            <Stack
                ref={ref}
                height="auto"
                alignItems="center"
                justifyContent="spaceBetween"
                paddingTop={{ default: '8', md: '10', lg: '12', xl: '14' }}
                paddingRight={{ default: '5', md: '10', lg: '10', xl: '16' }}
                paddingBottom={{ default: '8', md: '10', lg: '12', xl: '14' }}
                paddingLeft={{ default: '5', md: '10', lg: '10', xl: '16' }}
                borderRadius="2xl"
                background={GRADIENT}
                width="100%"
                direction={{ default: 'column', md: 'rowReverse' }}
                gap="10"
            >
                <Image
                    alt={translate<string>('logged_out_homepage.pro_banner_section.alt_freelancers_image')}
                    objectFit="contain"
                    height={{ default: '295px', md: '315px', lg: '380px', xl: '368px' }}
                    width="100%"
                    src={FREELANCERS_BG}
                />
                <Stack
                    direction="column"
                    color="white"
                    gap="0"
                    width={{ default: '100%', md: 'calc((100% - 40px) / 2)' }}
                    flexShrink={0}
                >
                    <Box marginBottom={{ default: '6', md: '8' }}>
                        <FiverrProLightLogo />
                    </Box>
                    <Stack direction="column" gap="6" marginBottom="8">
                        <Typography as="h2" color="inherit" className={styles.title}>
                            <I18n k="logged_out_homepage.pro_banner_section.title" />
                        </Typography>
                        <UnorderedList paddingLeft="5" gap="2" className={styles.bulletList}>
                            <ListItem>
                                <Text color="inherit">
                                    <I18n k="logged_out_homepage.pro_banner_section.bullet_1" />
                                </Text>
                            </ListItem>
                            <ListItem>
                                <Text color="inherit">
                                    <I18n k="logged_out_homepage.pro_banner_section.bullet_2" />
                                </Text>
                            </ListItem>
                            <ListItem>
                                <Text color="inherit">
                                    <I18n k="logged_out_homepage.pro_banner_section.bullet_3" />
                                </Text>
                            </ListItem>
                        </UnorderedList>
                    </Stack>
                    <Stack direction="column" gap="4" width={{ default: '100%', sm: 'fit-content' }}>
                        <Button
                            href={proLink}
                            target="_blank"
                            size="lg"
                            fullWidth
                            variant="filled"
                            colorScheme="white"
                            onClick={onButtonClick}
                        >
                            <I18n k="logged_out_homepage.pro_banner_section.cta" />
                        </Button>
                        <Stack gap="0" alignItems="center">
                            <Image
                                alt={translate<string>('logged_out_homepage.pro_banner_section.alt_money_back_image')}
                                width="38px"
                                objectFit="contain"
                                src={MONEY_BACK_ICON}
                                borderRadius="circle"
                            />
                            <Text className={styles.moneyBackText}>
                                <I18n k="logged_out_homepage.pro_banner_section.money_back_guarantee" />
                            </Text>
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>
        </Section>
    );
};

export { ProBannerSection };
export default SafeComponent(ProBannerSection);
